import moment from "moment/moment";
import momentTZ from "moment-timezone";
import { separateConstants } from "utilities/constants";
import { helperFunctions } from "utilities/helper-functions";

const { RequestFormStatusTypes, notActiveRequestsStates } = separateConstants;

const getTPRefByName = (tp, tpt, tradingPlatforms) => {
  if (tradingPlatforms)
    return tradingPlatforms.find(
      (t) => t.software === tp && t.softwareType === tpt
    )?.reference;
  return "";
};

const getBundleName = ({
  markets,
  marketdataProviders,
  marketBundles,
  tradingPlatformRef,
}) => {
  // Filter market data entitlements based on the trading platform reference
  const relevantEntitlements = marketdataProviders?.filter((entitlement) =>
    entitlement.tradingPlatformRefs.includes(tradingPlatformRef)
  );

  // Only use bundle-aware entitlements to determine bundle selection
  const bundledEntitlements = relevantEntitlements?.filter(
    (entitlement) => entitlement.bundle?.length
  );
  const matchedEntitlements = bundledEntitlements?.filter((entitlement) =>
    markets?.includes(entitlement.name)
  );

  // Determine the bundle name based on the matched entitlements
  if (marketBundles) {
    for (const bundle of marketBundles) {
      if (matchedEntitlements?.length > 0) {
        const isAllMatchedEntitlementsInBundle = matchedEntitlements.every(
          (entitlement) => entitlement.bundle.includes(bundle.name)
        );

        if (isAllMatchedEntitlementsInBundle) {
          return bundle.name; // All matched entitlements are in this bundle
        }
      }
    }
  }

  // If no bundle matches all conditions, return "Custom"
  return "Custom";
};

const getEffectiveDate = (requestedMarketsData) => {
  if (notActiveRequestsStates.includes(requestedMarketsData?.status)) {
    return moment().add(1, "M").startOf("month").format("YYYY-MM-DD");
  }
  return moment(requestedMarketsData?.schedule.duedate).format("YYYY-MM-DD");
};

const setToPrimaryDataHandler = ({
  requestedMarketsData,
  setLocalState,
  localState,
  setPrimaryEntitlements,
  extensions,
  getMarkets,
}) => {
  const getMarketsFromExtHandler = (type) => {
    return getMarketsFromExt(type, requestedMarketsData, extensions);
  };
  const primeMarkets = getMarketsFromExtHandler("ext");
  let markets = getMarketsFromExtHandler("ext");
  const addMarkets = [];

  const removeMarkets = getMarketsFromExtHandler("remove");
  // set add markets
  if (requestedMarketsData.status !== RequestFormStatusTypes.COMPLETED) {
    getMarketsFromExtHandler("add")?.forEach((m) => {
      if (!getMarketsFromExtHandler("ext").includes(m)) {
        addMarkets.push(m);
      }
    });
  }
  // set localState markets
  if (!!removeMarkets?.length) {
    markets = markets.filter((m) => !removeMarkets.includes(m));
  }
  if (!!addMarkets?.length) {
    addMarkets.forEach((m) => !markets.includes(m) && markets.push(m));
  }

  if (getMarkets) {
    return markets;
  }

  setLocalState({
    ...localState,
    markets: markets,
    addMarkets,
    effectiveDate: getEffectiveDate(requestedMarketsData),
    removeMarkets: removeMarkets,
  });
  setPrimaryEntitlements(primeMarkets);
};

const getMarketsFromExt = (type, requestedMarketsData, extensions) => {
  if (type === "remove")
    return requestedMarketsData.ext?.removeMarkets?.map((m) => m.market);
  if (type === "add")
    return requestedMarketsData.ext?.addMarkets?.map((m) => m.market);
  if (type === "ext") {
    if (!!extensions?.length) {
      return extensions
        .find((e) => e.code === "trading/marketdata")
        ?.ext?.markets.map((m) => m.market);
    } else {
      return [];
    }
  }
  return requestedMarketsData.ext?.addMarkets?.map((m) => m.market);
};

const onMarketStatusChangeHandler = ({
  name,
  value,
  reference,
  depRef,
  consistsOf = [],
  mutuallyExclusive = [],
  localState,
  copyLocalState,
  setLocalState,
  filterMarketsToTradingPlatform,
  primaryEntitlements,
  setDataWasChanged,
  requestedMarketsData = { status: "COMPLETED" },
  setChosenButton,
}) => {
  const marketItemsByRef = new Map(
    filterMarketsToTradingPlatform?.map((m) => [m.reference, m]) || []
  );
  const marketItemsByName = new Map(
    filterMarketsToTradingPlatform?.map((m) => [m.name, m]) || []
  );
  const normalizeRefs = (refs) => (Array.isArray(refs) ? refs : []);
  const resolveMarketName = (refOrName) =>
    marketItemsByRef.get(refOrName)?.name || marketItemsByName.get(refOrName)?.name;
  const resolveMarketRef = (refOrName) =>
    marketItemsByName.get(refOrName)?.reference || marketItemsByRef.get(refOrName)?.reference;
  const addMarketsByRefs = (refs, marketList) => {
    normalizeRefs(refs).forEach((refOrName) => {
      const marketName = resolveMarketName(refOrName);
      if (marketName && !marketList.includes(marketName)) {
        marketList.push(marketName);
      }
    });
  };
  const removeMarketsByRefs = (refs, marketList) => {
    const namesToRemove = normalizeRefs(refs)
      .map((refOrName) => resolveMarketName(refOrName))
      .filter(Boolean);
    return marketList.filter((m) => !namesToRemove.includes(m));
  };
  const isIncludedBySelectedParent = (childRef, childName, selectedMarkets) =>
    selectedMarkets.some((selectedName) =>
      normalizeRefs(marketItemsByName.get(selectedName)?.consistsOf).some(
        (refOrName) => refOrName === childRef || refOrName === childName
      )
    );
  const isRequiredBySelectedDependency = (childRef, selectedMarkets) =>
    selectedMarkets.some((selectedName) =>
      normalizeRefs(marketItemsByName.get(selectedName)?.dependencyRefs).includes(
        childRef
      )
    );
  const shouldKeepMarket = (childRef, childName, selectedMarkets) =>
    isIncludedBySelectedParent(childRef, childName, selectedMarkets) ||
    isRequiredBySelectedDependency(childRef, selectedMarkets);

  let newMarkets =
    localState && localState.markets ? [...localState.markets] : [];
  const newRemoveMarkets = [];
  const newAddMarkets = [];
  if (value) {
    if (normalizeRefs(depRef).length) {
      depRef.forEach((ref) => {
        if (
          !newMarkets.includes(
            filterMarketsToTradingPlatform.find((m) => m.reference === ref)
              ?.name
          )
        ) {
          newMarkets.push(
            filterMarketsToTradingPlatform.find((m) => m.reference === ref).name
          );
        }
      });
    }
    addMarketsByRefs(consistsOf, newMarkets);
    newMarkets.push(name);
    if (normalizeRefs(mutuallyExclusive).length) {
      const exclusiveNames = normalizeRefs(mutuallyExclusive)
        .map((ref) => marketItemsByRef.get(ref)?.name)
        .filter(Boolean);
      newMarkets = newMarkets.filter((m) => !exclusiveNames.includes(m));
      exclusiveNames.forEach((exclusiveName) => {
        const exclusiveRef = marketItemsByName.get(exclusiveName)?.reference;
        if (exclusiveRef) {
          const marketsToFilter = new Set(
            filterMarketsToTradingPlatform
              .filter((el) => el.dependencyRefs.includes(exclusiveRef))
              .map((el) => el.name)
          );
          newMarkets = newMarkets.filter((m) => !marketsToFilter.has(m));
        }
        if (
          exclusiveRef &&
          !shouldKeepMarket(exclusiveRef, exclusiveName, newMarkets)
        ) {
          newMarkets = removeMarketsByRefs(
            marketItemsByName.get(exclusiveName)?.consistsOf,
            newMarkets
          );
        }
      });
    }
  } else {
    if (reference) {
      const marketsToFilter = new Set(
        filterMarketsToTradingPlatform
          .filter((el) => el.dependencyRefs.includes(reference))
          .map((el) => el.name)
      );
      newMarkets = newMarkets.filter((m) => !marketsToFilter.has(m));
    }

    newMarkets = newMarkets.filter((m) => m !== name);
    if (reference) {
      normalizeRefs(consistsOf).forEach((refOrName) => {
        const marketName = resolveMarketName(refOrName);
        const marketRef = resolveMarketRef(refOrName);
        if (
          marketName &&
          !shouldKeepMarket(marketRef, marketName, newMarkets) &&
          newMarkets.includes(marketName)
        ) {
          newMarkets = newMarkets.filter((m) => m !== marketName);
        }
      });
    }
  }

  // compare markets chosen and primary
  const array2Sorted = newMarkets.slice().sort();
  if (
    primaryEntitlements.length === newMarkets.length &&
    primaryEntitlements
      .slice()
      .sort()
      .every((value, index) => value === array2Sorted[index]) &&
    !!newMarkets.length
  ) {
    if (notActiveRequestsStates.includes(requestedMarketsData.status)) {
      setDataWasChanged(() => false);
    }
  } else {
    setDataWasChanged(() => true);
  }

  // set addMarkets and removeMarkets
  filterMarketsToTradingPlatform.forEach((m) => {
    if (primaryEntitlements.includes(m.name) && !newMarkets?.includes(m.name))
      newRemoveMarkets.push(m.name);
    if (!primaryEntitlements?.includes(m.name) && newMarkets?.includes(m.name))
      newAddMarkets.push(m.name);
  });
  // detect no markets chosen
  if (newMarkets.length === 0) {
    setLocalState({
      ...localState,
      markets: [],
      addMarkets: newAddMarkets,
      removeMarkets: newRemoveMarkets,
    });
    if (copyLocalState) {
      copyLocalState([]);
    }
    // showModal(
    //   <ConfirmationModal
    //     isApplyButtonRed
    //     showEditModal
    //     closeModal={hideModal}
    //     onApplyClick={() => setCurrentPopupAction("no-markets")}
    //     labels={{
    //       title: "Remove all Market Data subscriptions?",
    //       body: "Do you really want to unsubscribe from all Market Data Entitlements?",
    //       buttons: { apply: "Yes", decline: "No" },
    //     }}
    //   />
    // );
  } else {
    setLocalState({
      ...localState,
      markets: newMarkets,
      addMarkets: newAddMarkets,
      removeMarkets: newRemoveMarkets,
    });
    if (copyLocalState) {
      copyLocalState(newMarkets);
    }
  }

  setChosenButton(newMarkets);
};

const getTotalPriceHandler = (markets, filterMarketsToTradingPlatform) => {
  if (markets) {
    const total = markets.reduce((acc, marketName) => {
      const market = filterMarketsToTradingPlatform?.find(
        (m) => m.name === marketName
      );
      return acc + (market?.price || 0);
    }, 0);

    return helperFunctions.currencyFormatter().format(total);
  } else return helperFunctions.currencyFormatter().format(0);
};

const expandMarketsSelection = (
  marketNames = [],
  filterMarketsToTradingPlatform = []
) => {
  const byName = new Map(
    filterMarketsToTradingPlatform.map((m) => [m.name, m])
  );
  const byRef = new Map(
    filterMarketsToTradingPlatform.map((m) => [m.reference, m])
  );
  const resolveName = (refOrName) =>
    byName.has(refOrName) ? refOrName : byRef.get(refOrName)?.name;

  const expanded = new Set(marketNames);
  const queue = [...expanded];

  while (queue.length) {
    const name = queue.shift();
    const item = byName.get(name);
    if (!item) continue;

    const refsToInclude = [
      ...(item.consistsOf || []),
      ...(item.dependencyRefs || []),
    ];

    refsToInclude.forEach((refOrName) => {
      const resolved = resolveName(refOrName);
      if (resolved && !expanded.has(resolved)) {
        expanded.add(resolved);
        queue.push(resolved);
      }
    });
  }

  return Array.from(expanded);
};

const setBundleColor = ({
  name,
  changingStatus,
  buttonItem,
  primaryBundle,
  requestedMarketsData,
}) => {
  if (name === buttonItem && buttonItem !== primaryBundle) {
    return "waiting-bundle";
  }
  if (
    name === buttonItem &&
    buttonItem === primaryBundle &&
    (changingStatus ||
      requestedMarketsData.status !== RequestFormStatusTypes.COMPLETED)
  ) {
    return "waiting-bundle";
  }
  if (name === primaryBundle) return "primary-bundle";
};

const setColorStatus = ({ name, primaryEntitlements, entitlements }) => {
  if (!primaryEntitlements?.includes(name) && entitlements?.includes(name))
    return "added-item market-checkmark-done";
  if (primaryEntitlements?.includes(name) && !entitlements?.includes(name))
    return "removed-item market-checkmark";
  return "";
};

const getRequiredRefs = (arrayOfRefsToFind, marketdataProviders) => {
  return arrayOfRefsToFind
    ?.map((r) => marketdataProviders.find((m) => m.reference === r)?.name)
    .filter(Boolean); // Filter out any undefined values
};

const getMarketWithName = ({ filterMarketsToTradingPlatform, name }) => {
  return filterMarketsToTradingPlatform
    ?.filter((m) => m.bundle.includes(name))
    .map((m) => m.name);
};

const getAccountStatusText = (defaultTraderIds, TGAccountsList) => {
  const found = TGAccountsList?.find(
    (t) => t.reference?.client === defaultTraderIds
  );
  if (found && found.status === "AccountEnabled") {
    return <span className="greyed-text">(enabled)</span>;
  }
  return <span className="account-disabled-status">(disabled)</span>;
};

const getMarketsToSend = ({ markets, filterMarketsToTradingPlatform }) =>
  filterMarketsToTradingPlatform
    .filter((f) => markets.includes(f.name))
    .map((f) => ({
      group: f.group,
      market: f.name,
      reference: f.reference,
    }));

const checkTwoMDArraysToIdentity = (array1 = [], array2 = []) => {
  const newMarkets = array1;
  const array2Sorted = newMarkets?.slice().sort();
  return (
    array2?.length === newMarkets?.length &&
    array2
      .slice()
      .sort()
      .every((value, index) => value === array2Sorted[index])
  );
};

const checkIfNotValidMarketdataRequest = (
  localValue,
  newMarkets,
  primaryMarkets
) => {
  if (checkTwoMDArraysToIdentity(newMarkets, primaryMarkets)) {
    return true;
  }

  return (
    localValue === "Invalid date" ||
    momentTZ(localValue).isBefore(new Date(), "day") ||
    momentTZ(localValue).isAfter(momentTZ().add(1, "year"))
  );
};

export {
  getTPRefByName,
  getBundleName,
  setToPrimaryDataHandler,
  getMarketsFromExt,
  getAccountStatusText,
  onMarketStatusChangeHandler,
  getTotalPriceHandler,
  setBundleColor,
  getRequiredRefs,
  getMarketWithName,
  setColorStatus,
  expandMarketsSelection,
  getMarketsToSend,
  checkTwoMDArraysToIdentity,
  checkIfNotValidMarketdataRequest,
};
